import { ValidationError } from '@multiplier/lib-shared-errors';
import { Injection } from '@multiplier/lib-shared-injection';
import { Result } from '@multiplier/lib-shared-result';
import Decimal from 'decimal.js';
import { z } from 'zod';
import { CurrencyCode, MinorUnits } from '../CurrencyConstants.js';
import { isValidDecimalForMinorUnits } from '../MonetaryUtils.js';
/** Regex for valid decimal strings (integer or decimal with optional minus) */
const DECIMAL_STRING_REGEX = /^-?\d+(\.\d+)?$/;
/**
 * Validates decimal places against currency minorUnits and adds issue if invalid.
 * Caller extracts currency, value, and path from their structure.
 */
function validateCurrencyMinorUnits(ctx, currency, value, path) {
    if (!isValidDecimalForMinorUnits(value, MinorUnits[currency])) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid decimal places for ${currency} (minorUnits=${MinorUnits[currency]}): "${value}"`,
            path,
        });
    }
}
/**
 * Validates that a currencyless zero has exactly "0" as its value.
 * Currencyless zeros cannot have decimal places since they have no minorUnits.
 */
function validateCurrencylessZeroValue(ctx, actualValue, path) {
    if (actualValue !== '0') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Currencyless zero must have value "0", got "${actualValue}"`,
            path,
        });
    }
}
/**
 * String-based schema for storing currency-less financial amounts
 * Validates that the value is a valid decimal number using Decimal.js
 */
export const ValueSchema = z
    .string()
    .min(1, 'Amount value cannot be empty')
    .refine((value) => value.trim().length > 0, 'Amount value cannot be empty')
    .refine((value) => {
    try {
        new Decimal(value);
        return true;
    }
    catch {
        return false;
    }
}, 'Amount value must be a valid number');
/**
 * Currency code schema - validates against actual currency codes from CURRENCY_DATA.
 * Normalizes input to uppercase before validation (handles lowercase from Stripe, etc.).
 * Parses to the CurrencyCode type (union of all valid currency code literals).
 *
 * This is the sole pathway for converting external strings to CurrencyCode.
 */
export const CurrencyCodeSchema = z
    .string()
    .transform((s) => s.toUpperCase())
    .pipe(z.enum(CurrencyCode));
/**
 * Injection between `CurrencyCode` and its canonical string representation.
 * `invert` accepts any string (e.g., values read from a BigQuery row) and
 * fails with `ValidationError` when it isn't a known currency code.
 */
export const currencyCodeInjection = new Injection((c) => c, (s) => {
    const parsed = CurrencyCodeSchema.safeParse(s);
    return parsed.success ? Result.ok(parsed.data) : Result.error(new ValidationError(`Invalid CurrencyCode: ${s}`));
});
/**
 * @deprecated Use CurrencyCodeSchema instead
 */
export const CurrencySchema = CurrencyCodeSchema;
/**
 * Currencyless zero - the only case where currency can be null.
 * Serves as an identity element for monetary addition: can be added to any
 * MonetaryAmount regardless of currency, yielding that amount unchanged.
 * Enables initializing accumulators without knowing the final currency upfront.
 *
 * The value must be exactly "0" (no decimal places) because currencyless
 * amounts effectively have minorUnits=0, so no decimal places are allowed.
 */
const CurrencylessZeroMonetaryAmountSchema = z.object({
    value: z.literal('0'),
    currency: z.literal(null),
});
/**
 * Amount with currency - can be zero or non-zero.
 * Zero with currency (e.g., $0 USD) preserves currency information.
 *
 * The value's decimal places are validated against the currency's minorUnits:
 * - No decimal places: always valid (e.g., "100" for any currency)
 * - Exactly minorUnits places: valid (e.g., "100.00" for USD with minorUnits=2)
 * - More than minorUnits places: valid only if last digit is non-zero (e.g., "100.123" for USD)
 * - Fewer than minorUnits places (except 0): invalid (e.g., "100.1" for USD)
 */
const WithCurrencyMonetaryAmountSchema = z
    .object({
    value: z.string().regex(DECIMAL_STRING_REGEX, 'Amount must be a valid decimal number'),
    currency: CurrencyCodeSchema,
})
    .superRefine((data, ctx) => {
    validateCurrencyMinorUnits(ctx, data.currency, data.value, ['value']);
});
/**
 * Metadata schema for tracking monetary operations
 * Used to trace how monetary values were calculated
 * Stores only the decimal string values (e.g., "100.00", "50.00") without currency
 */
export const MonetaryMetadataSchema = z.object({
    to: ValueSchema.optional(), // Starting value as decimal string
    from: ValueSchema.optional(), // Ending value as decimal string
    operand: z.enum(['add', 'subtract', 'multiply', 'negate']).optional(), // Operation performed
});
/**
 * Monetary amount schema with currency and optional metadata.
 * Two valid forms:
 * 1. Currencyless zero: { value: "0", currency: null } - identity element for addition
 * 2. With currency: { value: "<decimal>", currency: CurrencyCode } - any amount with currency
 */
export const MonetaryAmountSchema = z.union([CurrencylessZeroMonetaryAmountSchema, WithCurrencyMonetaryAmountSchema]);
const NonzeroLedgerBalanceSchema = z.object({
    value: z.string().regex(/^(([1-9]\d*(\.\d+)?)|(0\.\d*[1-9]\d*))$/, 'Amount must be valid positive number'),
    ledgerSide: z.union([z.literal('debit'), z.literal('credit')]),
});
const ZeroLedgerBalanceSchema = z.object({ value: z.string().regex(/^0(\.0+)?$/) });
export const LedgerBalanceSchema = z.union([NonzeroLedgerBalanceSchema, ZeroLedgerBalanceSchema]);
/**
 * Zero MonetaryLedgerBalance - validates the intersection of:
 * - LedgerBalance zero rules (value is zero, no ledgerSide)
 * - Currency-aware decimal rules (currencyless must be exactly "0", with-currency must pass predicate)
 */
const ZeroMonetaryLedgerBalanceSchema = z
    .object({
    value: ZeroLedgerBalanceSchema,
    currency: z.union([z.literal(null), CurrencySchema]),
})
    .superRefine((data, ctx) => {
    if (data.currency === null) {
        validateCurrencylessZeroValue(ctx, data.value.value, ['value', 'value']);
    }
    else {
        validateCurrencyMinorUnits(ctx, data.currency, data.value.value, ['value', 'value']);
    }
});
/**
 * Non-zero MonetaryLedgerBalance - validates the intersection of:
 * - LedgerBalance non-zero rules (strictly positive value, has ledgerSide)
 * - Currency rules (currency is required for non-zero)
 * - Currency-aware decimal rules (value must pass predicate for currency's minorUnits)
 */
const NonzeroMonetaryLedgerBalanceSchema = z
    .object({
    value: NonzeroLedgerBalanceSchema,
    currency: CurrencySchema,
})
    .superRefine((data, ctx) => {
    validateCurrencyMinorUnits(ctx, data.currency, data.value.value, ['value', 'value']);
});
export const MonetaryLedgerBalanceSchema = z.union([
    ZeroMonetaryLedgerBalanceSchema,
    NonzeroMonetaryLedgerBalanceSchema,
]);
// =============================================================================
// MoneyBag Schemas
// =============================================================================
/**
 * Schema for MoneyBag<Decimal> JSON representation.
 * Sparse record of currency codes to decimal strings (not every currency represented).
 * Validates each entry's decimal places against the currency's minorUnits.
 */
export const MonetaryAmountBagSchema = z
    .partialRecord(CurrencyCodeSchema, z.string().regex(DECIMAL_STRING_REGEX, 'Amount must be a valid decimal number'))
    .superRefine((data, ctx) => {
    for (const [currency, value] of Object.entries(data)) {
        if (value !== undefined) {
            validateCurrencyMinorUnits(ctx, currency, value, [currency]);
        }
    }
});
/**
 * Schema for MoneyBag<LedgerBalance> JSON representation.
 * Sparse record of currency codes to LedgerBalance JSON (no currency field in values).
 * Validates each entry's decimal places against the currency's minorUnits.
 */
export const MonetaryLedgerBalanceBagSchema = z
    .partialRecord(CurrencyCodeSchema, LedgerBalanceSchema)
    .superRefine((data, ctx) => {
    for (const [currency, balanceValue] of Object.entries(data)) {
        if (balanceValue !== undefined) {
            validateCurrencyMinorUnits(ctx, currency, balanceValue.value, [currency, 'value']);
        }
    }
});
//# sourceMappingURL=MonetarySchemas.js.map